import {
    doppeActionTypeCreateSettingsResolvingFunction,
    doppeActionTypeDefineMetadata
} from '../../client-server-common/doppe-action-types/doppe-action-type';
import type {RichTextContent, Values} from '@wix/devzai-utils-common';
import {
    LocationCoordinates,
    HideableValue,
    hideableValueCreateHidden
} from '@wix/devzai-utils-common';
import {
    DoppeActionContentSectionsSettings
} from '../../client-server-common/types/doppe-action-content-sections-settings';
import {DoppeActionCtaButtonsSettings} from '../../client-server-common/types/doppe-action-cta-buttons-settings';
import {doppeActionCtaButtonsResetSettingsForLock} from '../../client-server-common/types/doppe-action-cta-button';
import {
    doppeDtoActionSettingsRemoveInnerCtaContentForLockedAction
} from '../../client-server-common/types/doppe-dto-action';
import {
    BymoPagePropsContentSettingSemanticMeaning,
    BymoPagePropsContentSettingsMetadata,
    BymoPagePropsContentSettingType,
    bymoPagePropsCreateContentSettingMetadata,
    BymoPagePropsSpecialContentSettingMetadata
} from '../../client-server-common/bymo-page-props-metadata/bymo-page-props-metadata';

/**
 * Height options for the Location Map widget.
 */
export const LocationMapWidgetHeight = {
    Compact: 'Compact',
    Normal: 'Normal',
    Large: 'Large',
} as const;

export type LocationMapWidgetHeight = Values<typeof LocationMapWidgetHeight>;

/**
 * Pixel height values for each height option.
 */
const LocationMapWidgetHeightValues = {
    Compact: 200,
    Normal: 300,
    Large: 450,
} as const;

/**
 * Theme options for the Location Map widget.
 * Each theme applies a different visual style to the Google Map.
 */
export const LocationMapTheme = {
    /** Default Google Maps styling (light) */
    Standard: 'Standard',
    /** Muted grayscale tones */
    Silver: 'Silver',
    /** Warm vintage colors (cream, tan, olive) */
    Retro: 'Retro',
    /** Dark mode with dark roads and landscape */
    Dark: 'Dark',
    /** Deep blue/dark blue tones */
    Night: 'Night',
    /** Purple/eggplant tones */
    Aubergine: 'Aubergine',
} as const;

export type LocationMapTheme = Values<typeof LocationMapTheme>;

/**
 * Location Map action type namespace.
 * Contains all type definitions for the Location Map action.
 */
export namespace DoppeActionTypeLocationMap {

    /**
     * Base settings for the location map action.
     */
    export interface BaseActionSettings {
        /**
         * Title of the location map action (visible to users).
         */
        title: HideableValue<string>;

        /**
         * Description text displayed below the map.
         */
        description: HideableValue<RichTextContent>;

        /**
         * Geographical coordinates (latitude/longitude) for the map center.
         * Future: Can be extended to support AutoValue<T> for linked business locations.
         */
        locationCoordinates: LocationCoordinates | null;

        /**
         * Human-readable address text displayed to users.
         * Can be pre-filled from location's formattedAddress but is user-editable.
         */
        displayAddress: HideableValue<string>;

        /**
         * Suggested title from the map API (nameSuggestions[0]).
         * Used to pre-fill the action title during factory creation.
         */
        suggestedTitle: string | null;

        /**
         * Map zoom level (1-21 scale, where 21 is most zoomed in).
         * Default: 14
         */
        zoom: number;

        /**
         * Height of the map widget (Compact, Normal, or Large).
         * Default: Normal
         */
        height: LocationMapWidgetHeight;

        /**
         * Theme of the map widget.
         * Controls the visual styling of the Google Map (Standard, Silver, Retro, Dark, Night, Aubergine).
         * Default: Standard
         */
        mapTheme: LocationMapTheme;
    }

    /**
     * Complete action settings including base map settings, content sections, and CTA buttons.
     */
    export interface ActionSettings extends
        BaseActionSettings,
        DoppeActionContentSectionsSettings,
        DoppeActionCtaButtonsSettings {
    }
}

/**
 * Returns the pixel height value for a given height option.
 */
export function locationMapWidgetHeightGetPixelValue(height: LocationMapWidgetHeight): number {
    return LocationMapWidgetHeightValues[height];
}

/**
 * Content settings metadata for the location map action.
 * Defines which fields are considered content for AI features, translation, etc.
 */
export const doppeActionTypeLocationMapActionContentSettingsMetadata = {
    title: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Text, BymoPagePropsContentSettingSemanticMeaning.EntityTitle),
    description: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.RichText, BymoPagePropsContentSettingSemanticMeaning.EntityDescription),
    displayAddress: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Text, BymoPagePropsContentSettingSemanticMeaning.EntityAddress),
    locationCoordinates: BymoPagePropsSpecialContentSettingMetadata.ToBeConsidered,
    suggestedTitle: BymoPagePropsSpecialContentSettingMetadata.ToBeConsidered,
    zoom: BymoPagePropsSpecialContentSettingMetadata.Ignored,
    height: BymoPagePropsSpecialContentSettingMetadata.ToBeConsidered,
    mapTheme: BymoPagePropsSpecialContentSettingMetadata.ToBeConsidered
} satisfies BymoPagePropsContentSettingsMetadata<DoppeActionTypeLocationMap.BaseActionSettings>;

/**
 * Location Map action type metadata definition.
 * Defines behavior, defaults, and capabilities of the location map action.
 */
export const doppeActionTypeLocationMap = doppeActionTypeDefineMetadata<DoppeActionTypeLocationMap.ActionSettings>({
    id: 'io.bymo.action/location-map',
    resolveActionSettings: doppeActionTypeCreateSettingsResolvingFunction({
        title: '',
        description: '',
        locationCoordinates: null,
        displayAddress: '',
        suggestedTitle: null,
        zoom: 14,
        height: LocationMapWidgetHeight.Compact,
        mapTheme: LocationMapTheme.Standard,
        sections: [],
        ctaButtons: [],
    }),

    /**
     * Action settings metadata configuration.
     */
    actionSettingsMetadata: {
        contentSettingsMetadata: doppeActionTypeLocationMapActionContentSettingsMetadata,
        supportsActionCtaButtons: {
            customCtaButtonTypes: {}
        },
        supportsContentSections: {
            customContentSectionsTypes: {}
        },
        hideableContentSettingsNames: {
            description: true,
            title: true,
            displayAddress: true
        },
        bindableContentSettingsNames: {}
    },

    /**
     * Location Map action doesn't support list items.
     */
    actionListItemSettingsMetadata: false,

    /**
     * Location Map action opens a widget (not a modal or external link as primary action).
     */
    isWidgetOpener: true,

    /**
     * Location Map action doesn't open external links as its primary function.
     */
    isLinkOpener: false,

    /**
     * Location Map action can be rendered directly in a page.
     */
    canRenderInPage: true,

    /**
     * Don't skip reporting when button is clicked.
     */
    skipReportingOnGoalAchievedOnActionButtonClick: false,

    /**
     * Location Map action doesn't use media resources.
     */
    resolveUsedMedia: _actionSettings => [],

    /**
     * Location Map action doesn't have main media.
     */
    resolveMainMedia: _actionSettings => null,

    /**
     * Location Map action doesn't resolve to a main link (it's about showing a map, not navigating).
     */
    resolveMainLink: (_actionSettings, _viewerLang) => null,

    /**
     * Location Map action doesn't use business entities.
     */
    resolveUsedBusinessEntities: _actionSettings => [],

    /**
     * Prepare action settings for viewer (handle locked actions).
     */
    prepareForViewer: (actionSettings, options) => {
        doppeDtoActionSettingsRemoveInnerCtaContentForLockedAction(actionSettings, actionSettings => {
            actionSettings.locationCoordinates = null;
            actionSettings.displayAddress = hideableValueCreateHidden('');
            actionSettings.suggestedTitle = null;
            doppeActionCtaButtonsResetSettingsForLock(actionSettings);
        }, options);
    }
});
